import React, { useState, useEffect } from 'react';
import { API } from 'aws-amplify';
import {
  Avatar,
  Box,
  CardMedia,
  Checkbox,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import Fuse from 'fuse.js';
import { Area } from '../../../components/InteractiveMap/InteractiveMap.types';
import getUserAvatar from '../../../common/utils/getUserAvatar';
import { GamifyToast } from '../../../common/CustomToasts';

const SearchInput = styled('input')(({ theme }) => ({
  width: '100%',
  padding: '12px 16px',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '8px',
  fontSize: '16px',
  outline: 'none',
  '&:focus': {
    borderColor: theme.palette.primary.main,
  },
}));

const CustomDialog = styled(Dialog)(() => ({
  '& .MuiDialog-paper': {
    borderRadius: '12px',
    maxWidth: '600px',
    width: '100%',
    maxHeight: '80vh',
  },
}));

const ListContainer = styled(Box)(() => ({
  maxHeight: '400px',
  overflowY: 'auto',
  padding: '8px',
}));

const ListItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '12px',
  borderRadius: '8px',
  marginBottom: '8px',
  cursor: 'pointer',
  border: '1px solid transparent',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    border: `1px solid ${theme.palette.divider}`,
  },
}));

interface AreaAssignmentModalProps {
  area: Area | null;
  open: boolean;
  onClose: () => void;
  onUpdateAssignments: (
    areaId: string,
    users: any[],
    circles: any[]
  ) => Promise<void>;
}

export default function AreaAssignmentModal({
  area,
  open,
  onClose,
  onUpdateAssignments,
}: AreaAssignmentModalProps) {
  const [activeTab, setActiveTab] = useState(0);
  const [searchString, setSearchString] = useState('');
  const [usersList, setUsersList] = useState<any[]>([]);
  const [circlesList, setCirclesList] = useState<any[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
  const [selectedCircles, setSelectedCircles] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open && area) {
      fetchUsersAndCircles();
      // Initialize selected items with currently assigned users/circles
      setSelectedUsers(area.users || []);
      setSelectedCircles(area.circles || []);
    }
  }, [open, area]);

  const fetchUsersAndCircles = async () => {
    if (!area) return;

    setIsLoading(true);
    try {
      // Fetch users
      const usersQuery = `
        query MyQuery($orgID: ID!, $searchString: String!, $nextToken: String) {
          searchUsers(
            filter: {
              name: { wildcard: $searchString }
              orgID: { eq: $orgID }
              status: { ne: "deleted" }
            }
            limit: 750
            sort: { direction: asc, field: name }
            nextToken: $nextToken
          ) {
            items {
              id
              name
              status
              imageName
              imageType
              initials
              title
            }
            nextToken
          }
        }
      `;

      const circlesQuery = `
        query MyQuery($orgID: ID!, $searchString: String!, $nextToken: String) {
          searchCircles(
            filter: {
              name: { wildcard: $searchString }
              orgID: { eq: $orgID }
              isDeleted: { eq: false }
            }
            limit: 50
            sort: { direction: asc, field: name }
            nextToken: $nextToken
          ) {
            items {
              id
              name
              imageName
              imageType
            }
            nextToken
          }
        }
      `;

      // Fetch users
      let usersNextToken = null;
      let users: any[] = [];
      do {
        const usersResponse: any = await API.graphql({
          query: usersQuery,
          variables: {
            orgID: (global as any).me.orgID,
            searchString: '*',
            nextToken: usersNextToken,
          },
        });
        users = [...users, ...(usersResponse?.data?.searchUsers?.items || [])];
        usersNextToken = usersResponse?.data?.searchUsers?.nextToken;
      } while (usersNextToken);

      // Fetch circles
      let circlesNextToken = null;
      let circles: any[] = [];
      do {
        const circlesResponse: any = await API.graphql({
          query: circlesQuery,
          variables: {
            orgID: (global as any).me.orgID,
            searchString: '*',
            nextToken: circlesNextToken,
          },
        });
        circles = [
          ...circles,
          ...(circlesResponse?.data?.searchCircles?.items || []),
        ];
        circlesNextToken = circlesResponse?.data?.searchCircles?.nextToken;
      } while (circlesNextToken);

      // Process users
      const processedUsers = [];
      for (const user of users) {
        if (user.status === 'active') {
          const avatarFile = await getUserAvatar(
            user.imageName,
            user.imageType,
            true
          );
          processedUsers.push({
            ...user,
            avatarFile,
            type: 'user',
          });
        }
      }

      // Process circles
      const processedCircles = [];
      for (const circle of circles) {
        const avatarFile = await getUserAvatar(
          circle.imageName,
          circle.imageType,
          true
        );
        processedCircles.push({
          ...circle,
          avatarFile,
          type: 'circle',
        });
      }

      setUsersList(processedUsers);
      setCirclesList(processedCircles);
    } catch (error) {
      console.error('Error fetching users and circles:', error);
      GamifyToast.error('Failed to load users and teams');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUserToggle = (user: any) => {
    setSelectedUsers((prev) => {
      const isSelected = prev.some((u) => u.id === user.id);
      if (isSelected) {
        return prev.filter((u) => u.id !== user.id);
      } else {
        return [...prev, user];
      }
    });
  };

  const handleCircleToggle = (circle: any) => {
    setSelectedCircles((prev) => {
      const isSelected = prev.some((c) => c.id === circle.id);
      if (isSelected) {
        return prev.filter((c) => c.id !== circle.id);
      } else {
        return [...prev, circle];
      }
    });
  };

  const handleSubmit = async () => {
    if (!area) return;

    setIsSubmitting(true);
    try {
      await onUpdateAssignments(area.id, selectedUsers, selectedCircles);
      GamifyToast.success('Area assignments updated successfully');
      onClose();
    } catch (error) {
      console.error('Error updating area assignments:', error);
      GamifyToast.error('Failed to update area assignments');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getCurrentList = () => {
    return activeTab === 0 ? usersList : circlesList;
  };

  const getSelectedItems = () => {
    return activeTab === 0 ? selectedUsers : selectedCircles;
  };

  const handleItemToggle = (item: any) => {
    if (activeTab === 0) {
      handleUserToggle(item);
    } else {
      handleCircleToggle(item);
    }
  };

  const filteredItems = React.useMemo(() => {
    const list = getCurrentList();
    if (!searchString.trim()) return list;

    const fuse = new Fuse(list, {
      keys: ['name', 'title'],
      threshold: 0.3,
    });

    return fuse.search(searchString).map((result) => result.item);
  }, [getCurrentList(), searchString, activeTab]);

  if (!area) return null;

  return (
    <CustomDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Assign to Area: {area.name}</Typography>
          <Button onClick={onClose} sx={{ minWidth: 'auto', padding: '4px' }}>
            <Close />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
          >
            <Tab label={`Users (${selectedUsers.length})`} />
            <Tab label={`Teams (${selectedCircles.length})`} />
          </Tabs>
        </Box>

        <Box sx={{ mb: 2 }}>
          <SearchInput
            placeholder={`Search ${activeTab === 0 ? 'users' : 'teams'}...`}
            value={searchString}
            onChange={(e) => setSearchString(e.target.value)}
          />
        </Box>

        {isLoading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <ListContainer>
            {filteredItems.length === 0 ? (
              <Box textAlign="center" p={4}>
                <Typography color="textSecondary">
                  No {activeTab === 0 ? 'users' : 'teams'} found
                </Typography>
              </Box>
            ) : (
              filteredItems.map((item) => {
                const selectedItems = getSelectedItems();
                const isSelected = selectedItems.some((s) => s.id === item.id);

                return (
                  <ListItem
                    key={item.id}
                    onClick={() => handleItemToggle(item)}
                  >
                    <Box display="flex" alignItems="center" flex={1}>
                      {item.avatarFile ? (
                        <CardMedia
                          component="img"
                          src={item.avatarFile}
                          sx={{
                            borderRadius: 1000,
                            height: 40,
                            width: 40,
                            objectFit: 'contain',
                            margin: '5px 12px 5px 5px',
                          }}
                        />
                      ) : (
                        <Avatar
                          sx={{
                            backgroundColor: '#868686',
                            margin: '5px 12px 5px 5px',
                          }}
                        >
                          {item.initials || item.name[0]?.toUpperCase()}
                        </Avatar>
                      )}
                      <Box>
                        <Typography variant="body1" fontWeight={600}>
                          {item.name}
                        </Typography>
                        {item.title && (
                          <Typography variant="body2" color="textSecondary">
                            {item.title}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Checkbox
                      checked={isSelected}
                      sx={{
                        '&.Mui-checked': {
                          color: '#FF6A00',
                        },
                      }}
                    />
                  </ListItem>
                );
              })
            )}
          </ListContainer>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting}
          sx={{
            backgroundColor: '#FF6A00',
            '&:hover': {
              backgroundColor: '#E55A00',
            },
          }}
        >
          {isSubmitting ? (
            <CircularProgress size={20} sx={{ color: 'white' }} />
          ) : (
            'Update Assignments'
          )}
        </Button>
      </DialogActions>
    </CustomDialog>
  );
}
